(() => {
  if (window.__loreHomeVideoLoad) return;
  window.__loreHomeVideoLoad = true;
  const states = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const labels = document.currentScript?.dataset || {};
  let controlId = 0;
  function ensureId(element) {
    if (!element.id) {
      do { element.id = `lore-home-media-${++controlId}`; }
      while (document.getElementById(element.id) !== element);
    }
    return element.id;
  }

  const active = video => {
    const slide = video.closest('slideshow-slide');
    return !slide || (!slide.hidden && slide.getAttribute('aria-hidden') !== 'true');
  };
  const selector = 'main video.lore-home-video';
  const scope = video => video.closest('slideshow-slide, .hero');
  function controls(video) {
    const root = scope(video);
    if (!root) return;
    root.querySelectorAll('a[href="#play"]').forEach(button => {
      button.setAttribute('role', 'button');
      button.setAttribute('aria-controls', ensureId(video));
      button.dataset.lorePlayback = video.paused ? 'paused' : 'playing';
      button.style.backgroundImage = video.paused ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='white' d='M3 1L15 8 3 15Z'/%3E%3C/svg%3E\")" : ''; 
      button.setAttribute('aria-label', video.paused ? (labels.playLabel || 'Play video') : (labels.pauseLabel || 'Pause video'));
    });
    root.querySelectorAll('a[href="#mute"]').forEach(button => {
      button.dataset.loreMuted = String(video.muted);
      button.setAttribute('role', 'button');
      button.setAttribute('aria-controls', ensureId(video));
      button.setAttribute('aria-label', video.muted ? (labels.unmuteLabel || 'Unmute video') : (labels.muteLabel || 'Mute video'));
    });
    const slideshow = video.closest('slideshow-component');
    if (slideshow) root.querySelectorAll('a[href="#previous"], a[href="#next"]').forEach(button => {
      button.setAttribute('role', 'button');
      const next = button.getAttribute('href') === '#next';
      button.setAttribute('aria-label', next ? (labels.nextLabel || 'Next slide') : (labels.previousLabel || 'Previous slide'));
      button.setAttribute('aria-controls', ensureId(slideshow));
    });
  }
  function sync(video, state) {
    const shouldPlay = state.visible && active(video) && !document.hidden &&
      !state.userPaused && (state.autoplay || state.userStarted) && (!motion.matches || state.userStarted);
    if (shouldPlay) {
      if (video.preload === 'none') video.preload = 'metadata';
      if (video.paused && !state.pending) {
        const request = {};
        state.pending = request;
        video.play().catch(() => {}).finally(() => {
          if (state.pending === request) state.pending = null;
          controls(video);
        });
      }
    } else {
      state.pending = null;
      if (!video.paused) { state.internalPause = true; video.pause(); }
    }
    controls(video);
  }
  const near = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(({target: video, isIntersecting}) => {
      if (isIntersecting && active(video) && !motion.matches && video.preload === 'none') video.preload = 'metadata';
    });
  }, {rootMargin: '250px 0px'}) : null;
  const viewport = near ? new IntersectionObserver(entries => {
    entries.forEach(({target: video, isIntersecting}) => {
      const state = states.get(video);
      if (state) { state.visible = isIntersecting; sync(video, state); }
    });
  }, {threshold: 0.01}) : null;
  function refresh() {
    const focused = document.activeElement;
    const focusedSlide = focused?.closest?.('slideshow-slide');
    const focusedControl = focused?.getAttribute?.('data-lore-home-control');
    for (const [video] of states) {
      if (!video.isConnected) {
        video.pause();
        near?.unobserve(video);
        viewport?.unobserve(video);
        states.delete(video);
      }
    }
    document.querySelectorAll(selector).forEach(video => {
      if (states.has(video)) return;
      const userStarted = false;
      const rect = userStarted ? video.getBoundingClientRect() : null;
      const visible = !viewport || Boolean(rect && rect.bottom > 0 && rect.top < innerHeight);
      const state = {visible, userPaused: false, userStarted, pending: false, internalPause: false, autoplay: true};
      if (!scope(video)?.querySelector('a[href="#play"]')) video.controls = true;
      states.set(video, state);
      ['play', 'pause', 'ended', 'volumechange'].forEach(type => video.addEventListener(type, () => {
        if (type === 'play') {
          if (video.controls && !state.pending) { state.userStarted = true; state.userPaused = false; }
          if (!state.visible || !active(video) || document.hidden || state.userPaused || (motion.matches && !state.userStarted)) {
            state.internalPause = true;
            video.pause();
          }
        }
        if (type === 'pause') {
          if (state.internalPause) state.internalPause = false;
          else if (video.controls) { state.userPaused = true; state.pending = null; }
        }
        controls(video);
      }));
      near?.observe(video);
      viewport?.observe(video);
    });
    if (!window.Shopify?.designMode) {
      const slides = new Set([...states.keys()].map(video => video.closest('slideshow-slide')).filter(Boolean));
      slides.forEach(slide => { slide.inert = slide.hidden || slide.getAttribute('aria-hidden') === 'true'; });
      if (focusedControl && slides.has(focusedSlide) && focusedSlide.inert) {
        const slideshow = focusedSlide.closest('slideshow-component');
        const selected = slideshow?.querySelector('slideshow-slide[aria-hidden="false"]:not([hidden])');
        selected?.querySelector(`[data-lore-home-control="${focusedControl}"]`)?.focus({preventScroll: true});
      }
    }
    states.forEach((state, video) => {
      const rect = video.getBoundingClientRect();
      state.visible = rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
      sync(video, state);
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.('a[href="#play"], a[href="#mute"]');
    if (!button) return;
    const root = button.closest('slideshow-slide, .hero');
    const videos = root ? [...root.querySelectorAll('video')].filter(video => states.has(video)) : [];
    if (!videos.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.getAttribute('href') === '#mute') {
      const muted = videos.some(video => !video.muted);
      videos.forEach(video => { video.muted = muted; controls(video); });
      return;
    }
    const pause = videos.some(video => !video.paused);
    videos.forEach(video => {
      const state = states.get(video);
      if (!state) return;
      state.userPaused = pause;
      state.userStarted = !pause;
      if (!pause) state.visible = true;
      sync(video, state);
    });
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== ' ' || event.repeat) return;
    const button = event.target.closest?.('[data-lore-home-control]');
    const root = button?.closest('slideshow-slide, .hero');
    if (!root || ![...root.querySelectorAll('video')].some(video => states.has(video))) return;
    event.preventDefault();
    button.click();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, {once: true});
  else refresh();
  ['slideshow:select', 'shopify:section:load', 'shopify:section:unload'].forEach(type => document.addEventListener(type, refresh));
  document.addEventListener('visibilitychange', () => states.forEach((state, video) => sync(video, state)));
  motion.addEventListener('change', () => {
    states.forEach((state, video) => { state.userStarted = false; sync(video, state); });
  });
  const main = document.querySelector('main');
  if (main) new MutationObserver(records => {
    if (records.some(record => record.type === 'attributes' && record.target.matches?.('slideshow-slide') ||
      record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(node =>
        node.nodeType === 1 && (node.matches?.('video.lore-home-video') || node.querySelector?.('video.lore-home-video'))))) refresh();
  }).observe(main, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden']});
})();
